import { logger } from "./logger";

/**
 * THE START SIGNAL FOR A SPOTLIGHT WALKTHROUGH.
 *
 * Three unrelated places need to say "walk this person through the images screen": the
 * support panel (when the model emits a walkthrough marker), the per-screen "?" affordance,
 * and — in future — anything else that wants to teach rather than tell. None of them can
 * reach the overlay directly: it is mounted once, at the app root, by `SupportAssistantHost`,
 * which is not an ancestor of any of them.
 *
 * So this is a module-level bus, the same shape as `services/regionRuntime.ts`: mutable
 * module state, plain exported functions, and a reset for tests. Deliberately NOT a React
 * context — a context would make every caller a descendant of a provider, which is the exact
 * coupling this avoids, and would re-render the tree on a value that changes a handful of
 * times per session.
 *
 * ONE LISTENER, NOT A SET. A walkthrough is a modal, full-screen experience; two overlays
 * running at once is not a state anyone wants, and a `Set` of listeners would quietly permit
 * it. A second `subscribeWalkthrough` replaces the first, which is what a re-mount of the
 * host does and is the only case that legitimately arises.
 */
/**
 * `scan` asks for the DYNAMIC tour: the overlay scans the screen the user is on for its controls
 * and describes each (`utility/controlScanner.ts`, `utility/dynamicTour.ts`) instead of running
 * the curated steps. Only meaningful for the screen the user is standing on — the `?` is the
 * caller. Absent means the curated walkthrough, exactly as before.
 */
export interface WalkthroughOptions {
  readonly scan?: boolean;
}

type WalkthroughListener = (key: string, options?: WalkthroughOptions) => void;

let listener: WalkthroughListener | null = null;

/**
 * A request that arrived before anyone was listening.
 *
 * A queue of exactly ONE, and only because of mount ordering: the host mounts the overlay in
 * the same commit as the panel that may already be asking for a walkthrough, and effects run
 * child-first. Dropping that request would present as "the button did nothing the first time"
 * — the single worst failure mode for a help feature. It is replaced rather than appended,
 * because if two arrived before the overlay existed, the second is the one the user meant.
 *
 * It is cleared by the first subscriber and never expires: there is no correct time to decide
 * a pending request has gone stale, and the only thing that can consume it is the overlay.
 */
let pending: { key: string; options?: WalkthroughOptions } | null = null;

/**
 * Ask for a walkthrough by SCREEN KEY (a key from `utility/routes.ts`).
 *
 * This performs no access check of its own, on purpose. The gate is `canReachScreen`, and it
 * lives in the overlay where the user slice is readable — putting a second copy here would
 * create two answers to keep correct, which is precisely what `routes.ts` exists to prevent.
 * A key nobody can reach therefore still gets this far and is declined once, visibly, by the
 * thing that can explain why.
 */
export const startWalkthrough = (key: string, options?: WalkthroughOptions): void => {
  const wanted = (key || "").trim();
  if (!wanted) return;

  if (!listener) {
    // Not an error: see `pending`. Warned so a rising count in the field is visible, because
    // the other reading of this line is "the overlay is not mounted at all".
    logger.warn(
      `walkthroughRunner: no overlay is listening; holding "${wanted}" until one mounts.`
    );
    pending = { key: wanted, options };
    return;
  }

  // The one-argument call is kept when there are no options, so a listener that predates them
  // (and every test that pins the call) sees exactly what it always did.
  if (options) listener(wanted, options);
  else listener(wanted);
};

/**
 * Register the overlay. Returns an unsubscribe, which the overlay calls on unmount.
 *
 * The unsubscribe checks identity before clearing: if a remount has already installed a new
 * listener, the outgoing component's cleanup must not tear down its replacement. Same hazard
 * the card action menu's generation stamp exists for.
 */
export const subscribeWalkthrough = (fn: WalkthroughListener): (() => void) => {
  listener = fn;

  if (pending) {
    const queued = pending;
    pending = null;
    if (queued.options) fn(queued.key, queued.options);
    else fn(queued.key);
  }

  return () => {
    if (listener === fn) {
      listener = null;
    }
  };
};

/** Test seam: drop the listener and any queued request between cases. */
export const __resetWalkthroughRunner = (): void => {
  listener = null;
  pending = null;
};
